import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { lightStyles } from '../common/OrgSelect';
import { fetchProxyOrganizations, PROXYABLE_ROLES } from '../../services/demoProxyService';
import { UserRole } from '../../types';
import { createLogger } from '../../utils/logger';
import { roleLabel } from '../../utils/roleLabel';
import './DemoProxySwitcher.scss';

type OrgOption = { value: string; label: string };

const SEARCH_DEBOUNCE_MS = 300;

// lightStyles sets a z-index on `menu`, but a portalled menu is positioned by
// `menuPortal` instead — without this it renders behind the header dropdown.
const selectStyles = {
  ...lightStyles,
  menuPortal: (base: any) => ({ ...base, zIndex: 2200 }),
};

const logger = createLogger('DemoProxySwitcher');


interface DemoProxySwitcherProps {
  /** Called after a successful switch so the parent dropdown can close itself. */
  onSwitched?: () => void;
}

/**
 * "View as" control for demos — lets a super admin act as a lower role inside a
 * chosen organization without a second account.
 *
 * Rendered inside the user dropdown, and only for real super admins. The proxy is
 * server-side, so a switch is a round trip plus an auth refresh; the whole app then
 * behaves as the demonstrated role, backend included.
 */
const DemoProxySwitcher: React.FC<DemoProxySwitcherProps> = ({ onSwitched }) => {
  const { t } = useTranslation();
  const { realRole, isProxyActive, userRole, userData, startDemoProxy, stopDemoProxy } = useAuth();

  const [role, setRole] = useState<UserRole>('trainee');
  const [org, setOrg] = useState<OrgOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = realRole === 'super_admin';
  const orgId = org?.value ?? '';

  // Pre-select whatever is being demoed now, so re-opening the menu mid-demo shows the
  // current state rather than resetting. The org's name comes from the user payload
  // (GET /user/{uid} mirrors the proxy), so no extra lookup is needed to label it.
  useEffect(() => {
    if (!isProxyActive) return;
    if (userRole) setRole(userRole);
    const activeOrgId = userData?.organization_id;
    if (activeOrgId) {
      setOrg({ value: activeOrgId, label: userData?.organization?.name || activeOrgId });
    }
  }, [isProxyActive, userRole, userData?.organization_id, userData?.organization?.name]);

  // Debounced server-side search. The org list is platform-wide and can run to
  // thousands, so it is never loaded whole — `defaultOptions` fetches one page for the
  // initial dropdown and each keystroke re-queries.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The previous call's `resolve`, if a newer keystroke supersedes it before its
  // timeout fires — clearing the timeout alone leaves that promise (already handed to
  // react-select) pending forever, stranding the control in a loading state.
  const pendingResolveRef = useRef<((options: OrgOption[]) => void) | null>(null);

  const loadOptions = useCallback(
    (search: string): Promise<OrgOption[]> =>
      new Promise((resolve) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        pendingResolveRef.current?.([]);
        pendingResolveRef.current = resolve;
        debounceRef.current = setTimeout(async () => {
          pendingResolveRef.current = null;
          try {
            const items = await fetchProxyOrganizations(search);
            resolve(items.map((o) => ({ value: o.id, label: o.name })));
          } catch (e) {
            logger.error('Failed to load organizations for the demo proxy', e);
            setError(t('misc.demoProxy.loadOrgsFailed'));
            resolve([]);
          }
        }, SEARCH_DEBOUNCE_MS);
      }),
    [t],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingResolveRef.current?.([]);
    },
    [],
  );

  const handleStart = useCallback(async () => {
    if (!orgId) {
      setError(t('misc.demoProxy.pickOrgFirst'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await startDemoProxy(role, orgId);
      onSwitched?.();
    } catch (e: any) {
      logger.error('Failed to start the demo proxy', e);
      setError(e?.message || t('misc.demoProxy.switchFailed'));
    } finally {
      setBusy(false);
    }
  }, [orgId, role, startDemoProxy, onSwitched, t]);

  const handleStop = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await stopDemoProxy();
      onSwitched?.();
    } catch (e: any) {
      logger.error('Failed to end the demo proxy', e);
      setError(e?.message || t('misc.demoProxy.restoreFailed'));
    } finally {
      setBusy(false);
    }
  }, [stopDemoProxy, onSwitched, t]);

  if (!isSuperAdmin) return null;

  return (
    <div className="demo-proxy-switcher" data-testid="demo-proxy-switcher">
      <div className="demo-proxy-switcher__title">
        <Eye size={14} />
        {t('misc.demoProxy.viewAs')}
      </div>

      <label className="demo-proxy-switcher__label" htmlFor="demo-proxy-role">
        {t('common.role')}
      </label>
      <select
        id="demo-proxy-role"
        className="demo-proxy-switcher__select"
        value={role}
        disabled={busy}
        onChange={(e) => setRole(e.target.value as UserRole)}
      >
        {PROXYABLE_ROLES.map((r) => (
          <option key={r} value={r}>
            {roleLabel(r, t)}
          </option>
        ))}
      </select>

      <label className="demo-proxy-switcher__label" htmlFor="demo-proxy-org">
        {t('common.organization')}
      </label>
      <div className="demo-proxy-switcher__org">
        <AsyncSelect<OrgOption>
          inputId="demo-proxy-org"
          aria-label={t('common.organization')}
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          value={org}
          onChange={(opt) => setOrg(opt ?? null)}
          isDisabled={busy}
          styles={selectStyles}
          placeholder={t('orgSelect.searchPlaceholder')}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? t('misc.demoProxy.noMatchingOrgs') : t('misc.orgMultiSelect.noneAvailable')
          }
          // The header dropdown clips overflow, so the menu has to escape it.
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>

      {error && <p className="demo-proxy-switcher__error">{error}</p>}

      <div className="demo-proxy-switcher__actions">
        <button
          type="button"
          className="demo-proxy-switcher__button demo-proxy-switcher__button--primary"
          onClick={handleStart}
          disabled={busy || !orgId}
        >
          {isProxyActive ? t('misc.demoProxy.switch') : t('misc.demoProxy.start')}
        </button>
        {isProxyActive && (
          <button
            type="button"
            className="demo-proxy-switcher__button"
            onClick={handleStop}
            disabled={busy}
          >
            {t('misc.demoProxy.exit')}
          </button>
        )}
      </div>

      <p className="demo-proxy-switcher__hint">
        {t('misc.demoProxy.hint')}
      </p>
    </div>
  );
};

export default DemoProxySwitcher;
